import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { HiOutlineArrowLeft, HiOutlineArrowPath, HiOutlineCheck, HiOutlineCloudArrowUp, HiOutlineCodeBracket, HiOutlineCommandLine, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi2';
import { useAppInfo } from '../../../helpers/hooks/common-hook';
import { codehookConfigSave, codehookUploadArtifact, codehookUploadUrlGet, getCodehookConfig } from '../../../services/model/model-config-service';
import { FunctionInfo, CODEHOOK_FUNCTION_ARCHIVE_CODE_TYPE, CODEHOOK_FUNCTION_INLINE_CODE_TYPE } from '../../../dto/bot-model-config';
import { validateActionName } from '../../../helpers/utils/text-util';

const CODEHOOK_RUNTIMES = {
    'nodejs': [
        'nodejs24.x',
        'nodejs22.x',
        'nodejs18.x',
        'nodejs16.x',
        'nodejs14.x'
    ],
    'python': [
        'python3.10',
        'python3.9',
        'python3.8',
        'python3.7',
        'python3.6',
        'python2.7',
    ],
    'ruby': [
        'ruby3.2',
        'ruby2.7',
    ],
    'java': [
        'java17',
        'java11',
        'java8.al2',
        'java8',
    ],

    'go': [
        'go1.x',
    ]
    ,
    'dotnet': [
        'dotnetcore3.1',
        'dotnetcore2.1'
    ]
};
const runtimes = Object.values(CODEHOOK_RUNTIMES).flat();

const defaultFuncContent =
    `exports.handler = async (event) => {
  // TODO implement
  const { context, slots } = event;
  const { appId, sessionId, auth } = context;
  const response = {
    // TODO your payload
  };
  return response;
};
`;

const inputClass = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-400 focus:ring-gray-400 read-only:bg-gray-50 read-only:text-gray-500';
const secondaryButton = 'inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-40';
const languageFor = runtime => runtime.startsWith('nodejs') ? 'javascript' : runtime.startsWith('dotnet') ? 'csharp' : runtime.replace(/[\d.].*$/, '');
const envRows = values => Object.entries(values || {}).map(([key, val]) => ({ key, val }));

function initialForm(codehookId = '', info = new FunctionInfo({ code: { type: CODEHOOK_FUNCTION_INLINE_CODE_TYPE, inlineCode: defaultFuncContent } })) {
  return {
    codehookId, codehookType: 'fullfillment', runtime: info.runtime || '', handler: info.handler || '',
    codeFrom: info.code?.type || CODEHOOK_FUNCTION_INLINE_CODE_TYPE,
    inlineCode: info.code?.inlineCode ?? defaultFuncContent,
    environmentVariables: { ...info.environmentVariables },
  };
}

function Field({ label, name, error, hint, children, ...props }) {
  return <div className="space-y-2">
    <label htmlFor={name} className="block text-sm font-medium text-gray-800">{label}</label>
    {children || <input id={name} name={name} className={inputClass} aria-invalid={Boolean(error)} aria-describedby={error || hint ? `${name}-help` : undefined} {...props} />}
    {(error || hint) && <p id={`${name}-help`} className={`text-xs leading-5 ${error ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>{error || hint}</p>}
  </div>;
}

function FunctionEditor({ appId, codehookId }) {
  const [formData, setFormData] = useState(() => initialForm(codehookId));
  const [snapshot, setSnapshot] = useState(() => initialForm(codehookId));
  const [envKeys, setEnvKeys] = useState([]);
  const [savedInfo, setSavedInfo] = useState(null);
  const [saved, setSaved] = useState(Boolean(codehookId));
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(Boolean(codehookId));
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState('');
  const [reload, setReload] = useState(0);
  const busy = useRef(false);
  const formRef = useRef(null);
  const processing = Boolean(progress);
  const inline = formData.codeFrom === CODEHOOK_FUNCTION_INLINE_CODE_TYPE;
  const dirty = JSON.stringify(formData) !== JSON.stringify(snapshot) || JSON.stringify(envKeys) !== JSON.stringify(envRows(snapshot.environmentVariables)) || Boolean(file);

  useEffect(() => {
    if (!codehookId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    getCodehookConfig(appId, codehookId).then(result => {
      if (cancelled) return;
      if (!result?.functionInfo) throw new Error();
      const next = initialForm(codehookId, result.functionInfo);
      setFormData(next); setSnapshot(next); setEnvKeys(envRows(next.environmentVariables)); setSavedInfo(result.functionInfo);
    }).catch(() => { if (!cancelled) setLoadError('Unable to load this function. Please try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appId, codehookId, reload]);

  const update = useCallback((name, value) => {
    setFormData(previous => ({ ...previous, [name]: value }));
    setErrors(previous => ({ ...previous, [name]: undefined }));
    setSaveError(''); setSuccess('');
  }, []);
  const reset = () => {
    setFormData({ ...snapshot }); setEnvKeys(envRows(snapshot.environmentVariables)); setFile(null);
    setErrors({}); setSaveError(''); setSuccess('');
  };
  const updateEnv = (index, name, value) => {
    setEnvKeys(previous => previous.map((row, i) => i === index ? { ...row, [name]: value } : row));
    setErrors(previous => ({ ...previous, environment: undefined })); setSuccess('');
  };
  const chooseFile = selected => {
    if (!selected || busy.current) return;
    if (!selected.name.toLowerCase().endsWith('.zip')) {
      setErrors(previous => ({ ...previous, archive: 'Choose a ZIP archive.' })); return;
    }
    setFile(selected); setErrors(previous => ({ ...previous, archive: undefined })); setSuccess('');
  };
  const save = async event => {
    event.preventDefault();
    if (busy.current) return;
    const nextErrors = {};
    if (!validateActionName(formData.codehookId || '')) nextErrors.codehookId = 'Use letters, numbers, underscores or hyphens, without spaces.';
    if (!formData.runtime) nextErrors.runtime = 'Select a runtime.';
    if (!formData.handler.trim()) nextErrors.handler = 'Enter the function entry point.';
    if (inline && !formData.inlineCode.trim()) nextErrors.inlineCode = 'Enter your function code before saving.';
    if (!inline && !file && !(savedInfo?.code?.bucket || savedInfo?.code?.key || savedInfo?.code?.name)) nextErrors.archive = 'Choose a ZIP archive to upload.';
    const environmentVariables = {};
    for (const row of envKeys) {
      if (!row.key && !row.val) continue;
      if (!/^[a-zA-Z][a-zA-Z0-9_]+$/.test(row.key) || Object.prototype.hasOwnProperty.call(environmentVariables, row.key)) {
        nextErrors.environment = 'Use unique variable names of at least two characters, starting with a letter and containing only letters, numbers or underscores.';
      }
      Object.defineProperty(environmentVariables, row.key, { value: row.val, enumerable: true, configurable: true, writable: true });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      requestAnimationFrame(() => formRef.current?.querySelector('[aria-invalid="true"]')?.focus()); return;
    }
    busy.current = true; setSaveError(''); setSuccess(''); setProgress('Saving function…');
    try {
      if (!inline && file) {
        setProgress('Preparing upload…');
        const { url } = await codehookUploadUrlGet(appId, formData.codehookId, { codehookId: formData.codehookId, codehookType: formData.codehookType });
        if (!url) throw new Error('Unable to prepare the archive upload. Please try again.');
        setProgress('Uploading archive…');
        const response = await codehookUploadArtifact(url, file);
        if (!response?.ok) throw new Error('Archive upload failed. Please try again.');
      }
      setProgress('Saving function…');
      const payload = { ...formData, environmentVariables };
      const result = await codehookConfigSave(appId, payload);
      if (!(result?.status >= 200 && result.status < 300)) throw new Error('Unable to save the function. Your edits are still available; please try again.');
      setSnapshot(payload); setFormData(payload); setEnvKeys(envRows(environmentVariables)); setSaved(true);
      if (file && !inline) setSavedInfo(previous => ({ ...previous, code: { ...previous?.code, name: file.name } }));
      setFile(null); setSuccess('Function saved successfully.');
    } catch (error) {
      setSaveError(error.message || 'Unable to save the function. Please try again.');
    } finally {
      busy.current = false; setProgress('');
    }
  };

  return (
    <div className="min-h-full bg-gray-50/60 px-4 py-7 sm:px-6 lg:px-10">
      <form ref={formRef} onSubmit={save} noValidate className="mx-auto max-w-7xl space-y-6" aria-busy={loading || processing}>
        <Link to={`/app/${appId}/functions`} className="inline-flex items-center gap-2 rounded text-sm text-gray-500 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-gray-400"><HiOutlineArrowLeft className="h-4 w-4" />Functions</Link>
        <header className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-gray-200 bg-white shadow-sm dark:text-brand-lime"><HiOutlineCommandLine className="h-6 w-6" /></span>
            <div className="min-w-0"><h1 className="break-words text-2xl font-semibold tracking-tight text-gray-900">{saved ? formData.codehookId : 'Create function'}</h1><p className="mt-1 text-sm text-gray-500">Configure your runtime and write the code your agent will execute.</p></div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={reset} disabled={loading || processing || Boolean(loadError) || !dirty} className={secondaryButton}>Reset changes</button>
            <button type="submit" disabled={loading || processing || Boolean(loadError)} className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-50">{processing ? <HiOutlineArrowPath className="h-4 w-4 animate-spin" /> : <HiOutlineCheck className="h-4 w-4" />}{processing ? 'Saving…' : 'Save function'}</button>
          </div>
        </header>
        {(success || progress) && <p role="status" className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">{progress || success}</p>}
        {saveError && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">{saveError}</p>}
        {loading ? <div role="status" className="rounded-2xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">Loading function…</div>
          : loadError ? <div role="alert" className="rounded-2xl border border-gray-200 bg-white p-8 text-center"><p className="mb-4 text-sm text-red-600 dark:text-red-400">{loadError}</p><button type="button" onClick={() => setReload(value => value + 1)} className={secondaryButton}>Try again</button></div>
          : <fieldset disabled={processing} className="grid min-w-0 gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
            <div className="space-y-6">
              <section className="space-y-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between"><h2 className="text-sm font-semibold text-gray-900">Configuration</h2><span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-500">{dirty ? 'Unsaved changes' : saved ? 'Saved' : 'Draft'}</span></div>
                <Field label="Function name" name="codehookId" value={formData.codehookId} readOnly={saved} onChange={event => update('codehookId', event.target.value)} error={errors.codehookId} placeholder="e.g. copilot_tools" hint={saved ? 'Function names cannot be changed after creation.' : 'Use a unique name without spaces.'} />
                <Field label="Runtime" name="runtime" error={errors.runtime}>
                  <select id="runtime" value={formData.runtime} onChange={event => update('runtime', event.target.value)} aria-invalid={Boolean(errors.runtime)} className={inputClass}>
                    <option value="">Select runtime</option>
                    {formData.runtime && !runtimes.includes(formData.runtime) && <option value={formData.runtime}>{formData.runtime}</option>}
                    {Object.entries(CODEHOOK_RUNTIMES).map(([group, values]) => <optgroup key={group} label={group}>{values.map(value => <option key={value} value={value}>{value}</option>)}</optgroup>)}
                  </select>
                </Field>
                <Field label="Handler" name="handler" value={formData.handler} onChange={event => update('handler', event.target.value)} error={errors.handler} placeholder="index.handler" hint="The entry point called when your function runs." />
              </section>
              <section className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div><h2 className="text-sm font-semibold text-gray-900">Environment variables</h2><p className="mt-1 text-xs leading-5 text-gray-500">Values available to your function at runtime.</p></div>
                {envKeys.length === 0 && <p className="rounded-xl border border-dashed border-gray-200 p-4 text-xs text-gray-400">No environment variables configured.</p>}
                {envKeys.map((row, index) => <div key={index} className="space-y-2 rounded-xl bg-gray-50 p-3">
                  <div className="flex items-center gap-2"><input aria-label={`Variable ${index + 1} name`} aria-invalid={Boolean(errors.environment)} value={row.key} placeholder="VARIABLE_NAME" onChange={event => updateEnv(index, 'key', event.target.value)} className={inputClass} /><button type="button" aria-label={`Remove variable ${index + 1}`} onClick={() => { setEnvKeys(previous => previous.filter((_, i) => i !== index)); setSuccess(''); }} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-gray-400 dark:hover:bg-red-950/30"><HiOutlineTrash className="h-4 w-4" /></button></div>
                  <input aria-label={`Variable ${index + 1} value`} type="password" autoComplete="new-password" value={row.val} placeholder="Value" onChange={event => updateEnv(index, 'val', event.target.value)} className={inputClass} />
                </div>)}
                {errors.environment && <p role="alert" className="text-xs text-red-600 dark:text-red-400">{errors.environment}</p>}
                <button type="button" onClick={() => setEnvKeys(previous => [...previous, { key: '', val: '' }])} className={secondaryButton}><HiOutlinePlus className="h-4 w-4" />Add variable</button>
              </section>
            </div>
            <section className="min-w-0 self-start overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 p-5"><div><h2 className="text-sm font-semibold text-gray-900">Function code</h2><p className="mt-1 text-xs text-gray-500">Write code here or upload a ZIP archive.</p></div><label><span className="sr-only">Code source</span><select value={formData.codeFrom} onChange={event => update('codeFrom', event.target.value)} className={inputClass}><option value={CODEHOOK_FUNCTION_INLINE_CODE_TYPE}>Inline code</option><option value={CODEHOOK_FUNCTION_ARCHIVE_CODE_TYPE}>ZIP archive</option></select></label></div>
              {inline ? <>
                <div className="flex items-center gap-2 border-b border-gray-800 bg-[#1e1e1e] px-4 py-3 text-xs text-gray-400"><HiOutlineCodeBracket className="h-4 w-4" /><span>{formData.handler || 'Function code'}</span><span className="ml-auto">{formData.runtime}</span></div>
                <Editor height="min(60vh, 620px)" language={languageFor(formData.runtime)} value={formData.inlineCode} onChange={value => update('inlineCode', value ?? '')} theme="vs-dark" loading={<span className="p-8 text-sm text-gray-500">Loading code editor…</span>} options={{ minimap: { enabled: false }, fontSize: 13, padding: { top: 18, bottom: 18 }, scrollBeyondLastLine: false, wordWrap: 'on', automaticLayout: true, readOnly: processing, ariaLabel: 'Function code editor' }} />
                {errors.inlineCode && <p role="alert" className="p-4 text-sm text-red-600 dark:text-red-400">{errors.inlineCode}</p>}
              </> : <div className="p-6">
                <label onDragOver={event => event.preventDefault()} onDrop={event => { event.preventDefault(); chooseFile(event.dataTransfer.files[0]); }} className="flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 px-5 py-14 text-center focus-within:ring-2 focus-within:ring-gray-400">
                  <input type="file" aria-label="Upload ZIP archive" aria-invalid={Boolean(errors.archive)} accept=".zip,application/zip" onChange={event => { chooseFile(event.target.files[0]); event.target.value = ''; }} className="sr-only" />
                  <HiOutlineCloudArrowUp className="mb-4 h-9 w-9 text-gray-400" /><span className="max-w-full break-all text-sm font-medium text-gray-800">{file ? file.name : 'Drop your ZIP archive here, or browse'}</span><span className="mt-2 text-xs text-gray-500">{file ? `${(file.size / 1024).toFixed(1)} KB · Uploaded when you save` : 'Include your function code and its dependencies.'}</span>
                </label>
                {!file && savedInfo?.code?.type === CODEHOOK_FUNCTION_ARCHIVE_CODE_TYPE && <p className="mt-3 text-xs text-gray-500">The existing archive will be kept unless you choose a replacement.</p>}
                {errors.archive && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{errors.archive}</p>}
              </div>}
            </section>
          </fieldset>}
      </form>
    </div>
  );
}

export default function CodeHookFunctionManagePage() {
  const { appId } = useAppInfo();
  const { codehookId } = useParams();
  return <FunctionEditor key={`${appId}:${codehookId || 'new'}`} appId={appId} codehookId={codehookId} />;
}
